import { RefObject, SyntheticEvent, useEffect, useRef } from 'react';

import { useRemoteImage } from '../../hooks';

interface IRemoteImagePresenterParams {
  src: string;
  onLoad?(event?: SyntheticEvent): void;
  onError?(): void;
}

interface IRemoteImagePresenter {
  syntheticLoadEvent: SyntheticEvent<HTMLImageElement>;
  isLoading: boolean;
  isError: boolean;
  src: string;
  onLoad(e?: SyntheticEvent): void;
  onError(): void;
}

interface IUseRemoteImagePresenter {
  presenter: IRemoteImagePresenter;
  imgRef: RefObject<HTMLImageElement>;
}

export function useRemoteImagePresenter(
  params: IRemoteImagePresenterParams
): IUseRemoteImagePresenter {
  const imgRef = useRef<HTMLImageElement>(null);
  const { src, isLoading, isError } = useRemoteImage({ url: params.src });

  const presenter: IRemoteImagePresenter = {
    isError,
    src,

    get isLoading(): boolean {
      return isLoading;
    },

    get syntheticLoadEvent(): SyntheticEvent<HTMLImageElement> {
      const target = imgRef.current || new Image();

      return {
        target: target,
        currentTarget: target,
        cancelable: false,
        bubbles: false,
        nativeEvent: new Event('load'),
        defaultPrevented: false,
        eventPhase: 0,
        isTrusted: false,
        preventDefault: (): void => {
          return;
        },
        isDefaultPrevented: (): boolean => {
          return false;
        },
        stopPropagation: (): void => {
          return;
        },
        isPropagationStopped: (): boolean => {
          return false;
        },
        persist: (): void => {
          return;
        },
        timeStamp: Date.now(),
        type: 'load',
      };
    },

    onLoad(e?: SyntheticEvent): void {
      if (!params.onLoad) {
        return;
      }
      params.onLoad?.(e || presenter.syntheticLoadEvent);
    },

    onError(): void {
      params.onError?.();
    },
  };

  useEffect(() => {
    if (!presenter.isLoading && !presenter.isError && src) {
      presenter.onLoad();
    }
  }, [presenter.isLoading, src]);

  return { presenter, imgRef };
}
